import React, { type FC, useEffect, useMemo, useState } from "react";
import { CounterNumber as Number } from "./CounterNumber";
import { CounterChar as Char } from "./CounterChar";
import { Flex, Text } from "~/foundation/Chakra";
import { useInView } from "react-intersection-observer";

type CounterProps = {
	value: number;
	fontSize?: string;
	duration?: number;
	addDecimal?: boolean;
};

export const Counter: FC<CounterProps> = ({
	value,
	fontSize = "160px",
	duration = 10,
	addDecimal,
}) => {
	const [animate, setAnimate] = useState(false);

	const { ref, inView } = useInView({
		threshold: 0,
		triggerOnce: true,
	});

	useEffect(() => {
		if (inView && !animate) {
			setAnimate(true);
		}
	}, [inView]);

	const values = useMemo(() => {
		const valueStr = addDecimal ? value + ".0" : "" + value;
		let place = 0;
		return valueStr
			.split("")
			.reverse()
			.map((v) => {
				const integer = parseInt(v);
				const isNan = isNaN(integer);
				place = isNan ? place : place + 1;
				return {
					isInt: !isNan,
					value: isNan ? v : integer,
					place,
				};
			})
			.reverse();
	}, [value]);

	return (
		<Text as="span" fontSize={fontSize} display={"flex"} dir="ltr">
			<Flex
				pt="0.7rem"
				flexGrow={0}
				height=".7em"
				overflow="hidden"
				position="relative"
				ref={ref}
				justifyContent={"end"}
			>
				{values.map((v, idx) => (
					<React.Fragment key={idx}>
						{v.isInt ? (
							<Flex
								flexDir="column"
								alignItems="center"
								position="relative"
								width="0.5em"
								height="0.5em"
								justifyContent="center"
							>
								{[...Array(10).keys()].map((i) => (
									<Number
										place={v.place}
										value={(v.value as number) ?? 0}
										duration={duration}
										animate={animate}
										number={i}
										key={`${idx}-${i}`}
									/>
								))}
							</Flex>
						) : (
							<Char
								animate={animate}
								fontSize={fontSize}
								duration={duration}
								{...v}
								value={v.value as string}
							/>
						)}
					</React.Fragment>
				))}
			</Flex>
		</Text>
	);
};
